"use client";

import React from "react";
import { Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
const kpiData = [{
  id: "profit-factor",
  label: "Profit Factor",
  value: "1.40",
  description: "Gross profit divided by gross loss. A value greater than 1 indicates a profitable trading system."
}, {
  id: "max-drawdown",
  label: "MAX DRAWDOWN",
  value: "-24.01%",
  description: "The largest peak-to-trough decline in equity during the backtest period. Measures the worst-case loss experienced before recovery."
}, {
  id: "win-rate",
  label: "Win Rate",
  value: "33.32%",
  description: "The percentage of total trades that resulted in a profit over the backtest period."
}, {
  id: "total-return",
  label: "Total Return",
  value: "+37,368.89%",
  description: "The total percentage increase of the initial investment over the full backtest period (May 2021 - Apr 2026)."
}];
export default function PerformanceAnalyticsGrid() {
  return <section id="performance-analytics-grid" className="dark bg-background text-foreground py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,var(--color-accent)_0%,transparent_70%)] opacity-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-12 text-center sm:text-left">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
            Backtest Performance <span className="text-primary">Metrics</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg">Key performance indicators from a 5-year backtest of the ImperiumAlgo strategy on NASDAQ futures, using 1% risk per trade.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiData.map((kpi, i) => <div data-index={i} key={kpi.id}>
              <Card className="h-full bg-card backdrop-blur-md border-primary/10 shadow-[inset_0_4px_24px_rgba(0,0,0,0.6),0_0_15px_rgba(59,130,246,0.03)] relative overflow-hidden group transition-all duration-300 hover:border-primary/30 hover:shadow-[inset_0_4px_24px_rgba(0,0,0,0.6),0_0_25px_rgba(59,130,246,0.1)]">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0 px-6 pt-6">
                  <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    {kpi.label}
                  </CardTitle>
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="text-primary/60 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full p-1" aria-label={`Information about ${kpi.label}`}>
                          <Info className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[250px] bg-popover border-border/50 shadow-xl">
                        <p className="text-sm text-popover-foreground leading-relaxed">
                          {kpi.description}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardHeader>

                <CardContent className="px-6 pb-6">
                  <div className="text-4xl md:text-5xl font-mono font-bold text-foreground tracking-tighter drop-shadow-[0_0_8px_rgba(59,130,246,0.2)] transition-colors duration-300">
                    {kpi.value}
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}